import { Open } from '@drift-labs/icons';
import { ReactNode, useMemo } from 'react';
import Divider from 'src/components/primitives/Divider/Divider';
import { cj } from 'src/utils/classNames';
import useUserFeeTier from 'src/components/Modals/FeeTierModal/hooks/useUserFeeTier';
import {
	STAKED_TIERS,
	VOLUME_TIERS,
} from 'src/components/Modals/FeeTierModal/consts';
import { capitalize } from 'lodash';
import useDriftClient from 'src/hooks/useDriftClient';
import useModal from 'src/components/Modals/hooks/useModal';
import Image from 'next/image';

export default function PortfolioFeeTierCard() {
	const { show } = useModal();
	const driftClient = useDriftClient();
	const stateAccount = driftClient?.getStateAccount();
	const allFeeTiers = stateAccount?.perpFeeStructure?.feeTiers;

	// There are several tiers in `allFeeTiers` that have the `feeNumerator` zero'd out.
	// We want to ignore those, otherwise we can't determine if the user is on the highest tier.
	const feeTiers = useMemo(() => {
		return allFeeTiers?.filter((feeTier) => Boolean(feeTier.feeNumerator));
	}, [allFeeTiers]);

	const { userVolumeTierIndex, userStakedTierIndex } = useUserFeeTier();

	const {
		volumeTierLabel,
		stakedTierLabel,
		stakedTierDiscount,
		stakedTierDisplayAmount,
		takerFeePct,
		makerFeePct,
	} = (() => {
		const feeTier = feeTiers?.[userVolumeTierIndex];
		const stakedTier = STAKED_TIERS[userStakedTierIndex];

		if (
			!feeTier ||
			// When loading, `userStakedTierIndex` will be -1 and `stakedTier` won't exist.
			!stakedTier
		) {
			return {
				volumeTierLabel: '-',
				stakedTierLabel: '-',
				stakedTierDiscount: '-',
				stakedTierDisplayAmount: '-',
				takerFeePct: '-',
				makerFeePct: '-',
			};
		}

		const volumeTierLabel =
			userVolumeTierIndex === VOLUME_TIERS.length - 1
				? 'VIP'
				: `Tier ${userVolumeTierIndex + 1}`;

		const stakedTierLabel = capitalize(STAKED_TIERS[userStakedTierIndex].title);
		const stakedTierDiscount = STAKED_TIERS[userStakedTierIndex].discount * 100;
		const stakedTierDisplayAmount = STAKED_TIERS[userStakedTierIndex].display;

		const takerFee = feeTier.feeNumerator / feeTier.feeDenominator;
		const makerFee = -(
			feeTier.makerRebateNumerator / feeTier.makerRebateDenominator
		);

		const selectedStakedTierDiscount =
			STAKED_TIERS[userStakedTierIndex].discount;

		const takerFeeWithDiscount = takerFee * (1 - selectedStakedTierDiscount);
		const makerFeeWithDiscount = makerFee * (1 + selectedStakedTierDiscount);

		return {
			volumeTierLabel,
			stakedTierLabel,
			stakedTierDiscount,
			stakedTierDisplayAmount,
			takerFeePct: parseFloat((takerFeeWithDiscount * 100).toFixed(4)),
			makerFeePct: parseFloat((makerFeeWithDiscount * 100).toFixed(5)),
		};
	})();

	return (
		<button
			className={cj(
				'relative overflow-hidden rounded-sm p-md sm:p-2xl',
				'group flex flex-col gap-y-md text-left',
				'bg-[linear-gradient(93deg,var(--container-bg)_2.55%,var(--button-disabled)_101.36%)]',
				'hover:bg-[linear-gradient(93deg,var(--container-bg)_2.55%,var(--button-secondary-bg-hover)_101.36%)]',
				'dark:bg-[linear-gradient(93.08deg,var(--container-bg)_2.55%,var(--button-disabled)_101.36%)]',
				'dark:hover:bg-[radial-gradient(490.44%_134.28%_at_1.05%_99.06%,var(--button-disabled)_0%,_var(--button-disabled)_100%)]'
			)}
			onClick={() => show({ type: 'fee_tier', props: {} })}
			data-testid="portfolio-fee-tier-card"
		>
			<FeeTierCardSection
				label="Fees (Taker/Maker)"
				subLabel={volumeTierLabel}
				value={`${takerFeePct}% / ${makerFeePct}%`}
				valueEndElement={
					<span className="flex text-text-secondary transition-colors group-hover:text-text-default">
						<Open size={20} />
					</span>
				}
			/>

			<Divider className="w-2/3" />

			<FeeTierCardSection
				label="Staked Drift Booster"
				subLabel={stakedTierLabel}
				value={`${stakedTierDiscount}% / ${stakedTierDisplayAmount} DRIFT`}
			/>

			{/* Drift Logo */}
			<div className="absolute -right-8 bottom-1/2 translate-y-1/2 md:right-0">
				<div className="relative hidden h-43 w-43 dark:block">
					<Image src="/assets/icons/drift-fee-tier-dark.svg" alt="" fill />
				</div>
				<div className="relative block h-43 w-43 dark:hidden">
					<Image src="/assets/icons/drift-fee-tier-light.svg" alt="" fill />
				</div>
			</div>
		</button>
	);
}

interface Props {
	label: string;
	subLabel: string;
	value: string;
	valueEndElement?: ReactNode;
}

function FeeTierCardSection({
	label,
	subLabel,
	value,
	valueEndElement,
}: Props) {
	return (
		<div className="flex flex-col gap-y-sm">
			<span className="typo-b3 text-text-secondary">{label}</span>

			<div className="flex items-center gap-x-lg text-text-default">
				<span className="typo-h5">{subLabel}</span>

				<div className="flex items-center gap-x-xs">
					<span className="typo-b4">{value}</span>
					{valueEndElement}
				</div>
			</div>
		</div>
	);
}
